import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { HiOfficeBuilding, HiDocumentText } from "react-icons/hi";
import { FiLogIn } from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";
import { apiService } from "../../services/apiService";
import { FUND_SIZE_LABELS, FundSize } from "../../types/vcOnboarding";
import styles from "./VCSidebar.module.css";

const OfficeIcon = (): JSX.Element =>
  React.createElement(HiOfficeBuilding as React.ElementType, {
    className: styles.navIcon,
    "aria-hidden": true,
  });

const DocumentIcon = (): JSX.Element =>
  React.createElement(HiDocumentText as React.ElementType, {
    className: styles.navIcon,
    "aria-hidden": true,
  });

const LogOutIcon = (): JSX.Element =>
  React.createElement(FiLogIn as React.ElementType, {
    className: styles.logoutIcon,
    "aria-hidden": true,
  });

const BuildingIcon = (): JSX.Element =>
  React.createElement(HiOfficeBuilding as React.ElementType, {
    className: styles.fundIconSvg,
    "aria-hidden": true,
  });

interface VCSidebarProps {
  fundName?: string;
  fundSize?: FundSize;
}

const VCSidebar: React.FC<VCSidebarProps> = ({ fundName, fundSize }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const [updateCount, setUpdateCount] = useState<number | null>(null);

  useEffect(() => {
    const fetchUpdateCount = async () => {
      if (!user) return;
      
      try {
        const response = await apiService.getPortfolioUpdates({ limit: 1 });
        if (response.success && response.pagination) {
          setUpdateCount(response.pagination.total);
        }
      } catch (error) {
        console.error("Error fetching update count:", error);
      }
    };

    fetchUpdateCount();
  }, [user]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <div className={styles.sidebar}>
      {/* Branding */}
      <div className={styles.branding}>
        <div className={styles.logoSection}>
          <div>
            <div className={styles.logoText}>
              SendUpdates<span className={styles.logoHighlight}>.ai</span>
            </div>
            <div className={styles.portalLabel}>VC Portal</div>
          </div>
        </div>
      </div>

      {/* Fund Info */}
      {fundName && (
        <div className={styles.fundInfo}>
          <BuildingIcon />
          <div>
            <div className={styles.fundName}>{fundName}</div>
            {fundSize && (
              <div className={styles.fundSize}>
                {FUND_SIZE_LABELS[fundSize]}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className={styles.nav}>
        <NavLink
          to="/vc-dashboard/portfolio"
          className={({ isActive }) =>
            `${styles.navItem} ${
              isActive || location.pathname === "/vc-dashboard"
                ? styles.active
                : ""
            }`
          }
        >
          <OfficeIcon />
          <span className={styles.navLabel}>Portfolio</span>
        </NavLink>
        <NavLink
          to="/vc-dashboard/updates"
          className={({ isActive }) =>
            `${styles.navItem} ${isActive ? styles.active : ""}`
          }
        >
          <DocumentIcon />
          <span className={styles.navLabel}>Updates</span>
          {updateCount !== null && updateCount > 0 && (
            <span className={styles.badge}>{updateCount}</span>
          )}
        </NavLink>
      </nav>

      {/* Logout Button */}
      <button onClick={handleLogout} className={styles.logoutButton}>
        <LogOutIcon />
        <span>Logout</span>
      </button>
    </div>
  );
};

export default VCSidebar;
